import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export type AppCardProps = {
  id: number | string;
  name: string;
  description?: string;
  icon?: React.ReactNode; // could be an <img /> or svg icon
  isAssigned?: boolean; // whether added by user
  isAvailable?: boolean; // user role allows it
  onToggleAssign?: (id: number | string, next: boolean) => void;
  className?: string;
  /** When false, hide the assignment button (used in read-only contexts like MyApps widget) */
  showAssignButton?: boolean;
};

/**
 * AppCard displays an ppsTrak application with status/assignment actions.
 * Layout: Icon, Name, Description, Button (tight vertical spacing). No fixed sizes.
 */
export function AppCard({
  id,
  name,
  description,
  icon,
  isAssigned = false,
  isAvailable = true,
  onToggleAssign,
  className,
  showAssignButton = true,
}: AppCardProps) {
  const handleClick = React.useCallback(() => {
    if (!isAvailable) return; // do nothing if user not allowed
    onToggleAssign?.(id, !isAssigned);
  }, [id, isAssigned, isAvailable, onToggleAssign]);

  return (
    <Card
      className={cn(
        "aero:transition-colors aero:h-full aero:border-neutral-300 aero:flex aero:flex-col aero:items-center aero:text-center aero:px-5 aero:py-5 aero:gap-3",
        !isAvailable && "aero:bg-neutral-100 aero:pointer-events-none",
        className
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          "aero:mb-3 aero:flex aero:items-center aero:justify-center aero:rounded-md aero:p-2"
        )}
        aria-hidden
      >
        {icon}
      </div>
      {/* Name */}
      <CardHeader className="aero:px-0 aero:py-0 aero:mb-1 aero:w-full">
        <CardTitle className="aero:text-base aero:font-bold aero:w-full aero:text-center">
          {name}
        </CardTitle>
      </CardHeader>
      {/* Description */}
      {description && (
        <CardContent className="aero:px-0 aero:py-0 aero:mb-3 aero:w-full aero:flex-1">
          <CardDescription className="aero:text-sm aero:text-neutral-500 aero:leading-snug">
            {description}
          </CardDescription>
        </CardContent>
      )}
      {/* Button */}
      {showAssignButton && (
        <CardFooter className="aero:pt-0 aero:w-full aero:flex aero:justify-center aero:text-center aero:mt-auto">
          <Button
            onClick={handleClick}
            intent={isAssigned ? "secondary" : "primary"}
            design={isAssigned ? "outline" : "solid"}
            size="sm"
            className={cn(
              "aero:px-4 aero:py-5 aero:font-bold",
              isAssigned
                ? "aero:bg-neutral-200 aero:border-neutral-200 aero:text-blue-800 aero:hover:bg-blue-800/10"
                : "aero:bg-blue-800 aero:hover:bg-blue-800 aero:text-white",
              !isAvailable && "aero:bg-neutral-300 aero:text-neutral-50"
            )}
          >
            {isAssigned ? "Hide My App" : "Add to My App"}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

export default AppCard;
